import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  Upload,
  message
} from "antd";
import {
  BankOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  SyncOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText } from "../api";
import { formatCents, formatDate } from "../shared/money";
import MoneyInput from "../shared/MoneyInput";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { KIND_LABELS, PROVIDER_LABELS } from "./constants";
import {
  connectAccount,
  createAccount,
  deactivateAccount,
  importFile,
  syncAccount,
  updateAccount
} from "./api";
import { openExternal } from "../../../../native/files";

const MAPPING_FIELDS = [
  "bookingDate",
  "valueDate",
  "amount",
  "debit",
  "credit",
  "purpose",
  "counterpartName",
  "counterpartIban",
  "counterpartBic",
  "bookingText",
  "balance"
];
const FIELD_LABELS = {
  bookingDate: "Booking date",
  valueDate: "Value date",
  amount: "Amount",
  debit: "Debit column",
  credit: "Credit column",
  purpose: "Purpose",
  counterpartName: "Counterpart name",
  counterpartIban: "Counterpart IBAN",
  counterpartBic: "Counterpart BIC",
  bookingText: "Booking text",
  balance: "Balance"
};

/** Add / edit dialog for a bank account. */
const AccountModal = ({ open, account, presets, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isNew = !account;

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body = { ...values, csvMapping: { preset: values.preset || "" } };
      delete body.preset;
      const res = isNew ? await createAccount(body) : await updateAccount(account._id, body);
      message.success(isNew ? t("Bank account created") : t("Bank account updated"));
      onSaved(res.data.account);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isNew ? t("Add bank account") : t("Edit bank account")}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Save")}
      cancelText={t("Cancel")}
      rootClassName="acct-module"
      destroyOnClose
      forceRender
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: account?.name || "",
          iban: account?.iban || "",
          bic: account?.bic || "",
          bankName: account?.bankName || "",
          holder: account?.holder || "",
          kind: account?.kind || "current",
          provider: account?.provider || "csv",
          preset: account?.csvMapping?.preset || "",
          ledgerAccount: account?.ledgerAccount || "",
          balance: account?.balance ?? null
        }}
      >
        <Form.Item
          name="name"
          label={t("Name")}
          rules={[{ required: true, message: t("Name is required") }]}
        >
          <Input placeholder="Geschäftskonto" />
        </Form.Item>
        <Form.Item
          name="iban"
          label="IBAN"
          rules={[{ required: true, message: t("IBAN is required") }]}
        >
          <Input
            placeholder="DE89 3704 0044 0532 0130 00"
            disabled={!isNew && account?.counts && Object.keys(account.counts).length > 0}
          />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="bic" label="BIC">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bankName" label={t("Bank name")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="holder" label={t("Account holder")}>
          <Input />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="kind" label={t("Account type")}>
              <Select
                options={Object.entries(KIND_LABELS).map(([value, label]) => ({
                  value,
                  label: t(label)
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="provider" label={t("Source")}>
              <Select
                options={Object.entries(PROVIDER_LABELS).map(([value, label]) => ({
                  value,
                  label: t(label)
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="preset"
          label={t("CSV format")}
          extra={t("Leave empty to detect the format from the file header.")}
        >
          <Select
            allowClear
            options={(presets || []).map(p => ({ value: p.key, label: p.label }))}
          />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="ledgerAccount"
              label={t("Ledger account")}
              extra={isNew ? t("Empty: the next free bank account (1200, 1210, ...)") : null}
            >
              <Input placeholder="1200" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="balance" label={t("Bank balance")}>
              <MoneyInput />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

/** File upload with a column-mapping step for unknown CSV layouts. */
const ImportModal = ({ open, account, presets, onClose, onImported }) => {
  const { t } = useTranslation("accounting");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [delimiter, setDelimiter] = useState("");
  const [preset, setPreset] = useState("");
  const [saveMapping, setSaveMapping] = useState(true);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setDelimiter("");
    setPreset("");
  };

  const send = async withMapping => {
    if (!file) return message.warning(t("Choose a file first"));
    const fd = new FormData();
    fd.append("file", file);
    if (withMapping) {
      fd.append(
        "mapping",
        JSON.stringify({
          preset,
          delimiter: delimiter || preview?.delimiter || "",
          encoding: preview?.encoding || "",
          columns: mapping
        })
      );
      fd.append("saveMapping", saveMapping ? "true" : "false");
    } else if (preset) {
      fd.append("mapping", JSON.stringify({ preset }));
      fd.append("saveMapping", "true");
    }
    setBusy(true);
    try {
      const res = await importFile(account._id, fd);
      if (res.data.needsMapping) {
        setPreview(res.data);
        message.info(t("Unknown CSV layout: map the columns below"));
        return;
      }
      message.success(
        t("{{imported}} imported, {{skipped}} skipped, {{matched}} matched automatically", {
          imported: res.data.imported,
          skipped: res.data.skipped,
          matched: res.data.matched
        })
      );
      reset();
      onImported(res.data);
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const columns = (preview?.headers || []).map((h, i) => ({
    title: h || `#${i + 1}`,
    dataIndex: i,
    key: i,
    ellipsis: true
  }));
  const sample = (preview?.sample || []).map((row, i) => ({ key: i, ...row }));
  const mappingOk = mapping.bookingDate && (mapping.amount || (mapping.debit && mapping.credit));

  return (
    <Modal
      rootClassName="acct-module"
      open={open}
      title={`${t("Import statement")}: ${account?.name || ""}`}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={
        <div className="acct-actions" style={{ justifyContent: "flex-end" }}>
          {!preview ? (
            <Button type="primary" loading={busy} onClick={() => send(false)} disabled={!file}>
              {t("Import")}
            </Button>
          ) : (
            <Button type="primary" loading={busy} disabled={!mappingOk} onClick={() => send(true)}>
              {t("Import with this mapping")}
            </Button>
          )}
        </div>
      }
      width={preview ? 900 : 520}
      rootClassName="acct-module"
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Upload.Dragger
          beforeUpload={f => {
            setFile(f);
            setPreview(null);
            return false;
          }}
          maxCount={1}
          accept=".csv,.txt,.xml,.sta,.mt940"
          fileList={file ? [file] : []}
          onRemove={() => setFile(null)}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p>{t("CSV, MT940 or CAMT.053 / CAMT.052 file")}</p>
        </Upload.Dragger>
        {!preview ? (
          <Select
            allowClear
            placeholder={t("CSV format (auto)")}
            style={{ width: "100%" }}
            value={preset || undefined}
            onChange={v => setPreset(v || "")}
            options={(presets || []).map(p => ({ value: p.key, label: p.label }))}
          />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              message={t(
                "Assign the columns of this file. The mapping is saved on the account for the next import."
              )}
            />
            <ResponsiveTable
              mobileLayout="table"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={sample}
              scroll={{ x: true }}
            />
            <Row gutter={[12, 8]}>
              {MAPPING_FIELDS.map(field => (
                <Col xs={24} sm={12} md={8} key={field}>
                  <Typography.Text type="secondary">{t(FIELD_LABELS[field])}</Typography.Text>
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    value={mapping[field]}
                    onChange={v => setMapping(m => ({ ...m, [field]: v || "" }))}
                    options={(preview.headers || []).map((h, i) => ({
                      value: h || String(i),
                      label: h || `#${i + 1}`
                    }))}
                  />
                </Col>
              ))}
              <Col xs={24} sm={12} md={8}>
                <Typography.Text type="secondary">{t("Delimiter")}</Typography.Text>
                <Select
                  style={{ width: "100%" }}
                  value={delimiter || preview.delimiter}
                  onChange={setDelimiter}
                  options={[
                    { value: ";", label: ";" },
                    { value: ",", label: "," },
                    { value: "\t", label: "Tab" },
                    { value: "|", label: "|" }
                  ]}
                />
              </Col>
            </Row>
            <Select
              style={{ width: "100%", maxWidth: 360 }}
              value={saveMapping}
              onChange={setSaveMapping}
              options={[
                { value: true, label: t("Save mapping on the account") },
                { value: false, label: t("Use once") }
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

/** Account cards with balances, import and API sync. */
const AccountsTab = ({ accounts, presets, loading, canWrite, onChanged }) => {
  const { t } = useTranslation("accounting");
  const [editing, setEditing] = useState({ open: false, account: null });
  const [importing, setImporting] = useState({ open: false, account: null });
  const [busy, setBusy] = useState("");

  const sync = async account => {
    setBusy(account._id);
    try {
      const res = await syncAccount(account._id);
      message.success(
        t("{{imported}} imported, {{skipped}} skipped, {{matched}} matched automatically", res.data)
      );
      onChanged();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy("");
    }
  };

  const connect = async account => {
    setBusy(account._id);
    try {
      const res = await connectAccount(account._id, {});
      if (res.data.url) openExternal(res.data.url);
      else message.info(t("Consent renewed"));
      onChanged();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy("");
    }
  };

  const toggleActive = account => {
    Modal.confirm({
      title: t("Deactivate bank account?"),
      content: t("Transactions and postings are kept; the account is hidden from new imports."),
      okText: t("Yes"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deactivateAccount(account._id);
          onChanged();
        } catch (err) {
          message.error(errorText(err));
        }
      }
    });
  };

  return (
    <div>
      {canWrite ? (
        <PageToolbar
          actions={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing({ open: true, account: null })}
            >
              {t("Add bank account")}
            </Button>
          }
        />
      ) : null}
      {!loading && !accounts.length ? (
        <Card>
          <Empty
            description={t(
              "No bank account yet. Add your business account and import the first statement."
            )}
          />
        </Card>
      ) : null}
      <Row gutter={[16, 16]}>
        {accounts.map(a => {
          const diff = a.difference || 0;
          const open = (a.counts?.unmatched || 0) + (a.counts?.suggested || 0);
          const apiProvider = ["finapi", "gocardless"].includes(a.provider);
          const consentOk = a.consentExpiresAt && new Date(a.consentExpiresAt) > new Date();
          return (
            <Col xs={24} md={12} xl={8} key={a._id}>
              <Card
                style={{ height: "100%" }}
                title={
                  <Space>
                    <BankOutlined />
                    {a.name}
                  </Space>
                }
                extra={
                  canWrite ? (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setEditing({ open: true, account: a })}
                    >
                      {t("Edit")}
                    </Button>
                  ) : null
                }
              >
                <Typography.Text type="secondary" copyable={{ text: a.iban }} className="acct-num">
                  {a.iban.replace(/(.{4})/g, "$1 ").trim()}
                </Typography.Text>
                <Row gutter={[12, 12]} style={{ marginTop: 12 }} className="acct-kpi">
                  <Col xs={12}>
                    <Statistic title={t("Bank balance")} value={formatCents(a.balance)} />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {a.balanceDate ? formatDate(a.balanceDate) : "–"}
                    </Typography.Text>
                  </Col>
                  <Col xs={12}>
                    <Statistic
                      title={`${t("Ledger balance")} (${a.ledgerAccount})`}
                      value={formatCents(a.ledgerBalance)}
                    />
                    <Typography.Text
                      type={diff === 0 ? "success" : "danger"}
                      style={{ fontSize: 12 }}
                    >
                      {t("Difference")}: <span className="acct-num">{formatCents(diff)}</span>
                    </Typography.Text>
                  </Col>
                </Row>
                <div style={{ marginTop: 12 }}>
                  <Space wrap size={[4, 4]}>
                    <Tag>{t(KIND_LABELS[a.kind] || a.kind)}</Tag>
                    {!a.active ? <Tag>{t("inactive")}</Tag> : null}
                    <Tag color={open ? "gold" : "green"}>
                      {open} {t("open")}
                    </Tag>
                    <Tag>
                      {(a.counts?.matched || 0) + (a.counts?.split || 0)} {t("matched")}
                    </Tag>
                    <Tag>{t(PROVIDER_LABELS[a.provider] || a.provider)}</Tag>
                    {apiProvider ? (
                      <Tag color={consentOk ? "green" : "red"}>
                        {consentOk ? t("consent valid") : t("consent expired")}
                      </Tag>
                    ) : null}
                  </Space>
                </div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: 8 }}
                >
                  {t("Last import")}:{" "}
                  {a.lastImportedAt ? new Date(a.lastImportedAt).toLocaleString("de-DE") : "–"}
                </Typography.Text>
                {canWrite && a.active ? (
                  <div
                    className="acct-actions"
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid var(--ds-border-soft)"
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      onClick={() => setImporting({ open: true, account: a })}
                    >
                      {t("Import")}
                    </Button>
                    {apiProvider ? (
                      <Button
                        icon={<SyncOutlined />}
                        loading={busy === a._id}
                        onClick={() => sync(a)}
                        disabled={!consentOk}
                      >
                        {t("Sync")}
                      </Button>
                    ) : null}
                    {apiProvider ? (
                      <Button
                        icon={<LinkOutlined />}
                        loading={busy === a._id}
                        onClick={() => connect(a)}
                      >
                        {consentOk ? t("Renew consent") : t("Connect")}
                      </Button>
                    ) : null}
                    <Button danger type="text" onClick={() => toggleActive(a)}>
                      {t("Deactivate")}
                    </Button>
                  </div>
                ) : null}
              </Card>
            </Col>
          );
        })}
      </Row>
      <AccountModal
        key={editing.account?._id || "new"}
        open={editing.open}
        account={editing.account}
        presets={presets}
        onClose={() => setEditing({ open: false, account: null })}
        onSaved={() => {
          setEditing({ open: false, account: null });
          onChanged();
        }}
      />
      <ImportModal
        open={importing.open}
        account={importing.account}
        presets={presets}
        onClose={() => setImporting({ open: false, account: null })}
        onImported={() => {
          setImporting({ open: false, account: null });
          onChanged();
        }}
      />
    </div>
  );
};

export default AccountsTab;
